// Utility functions
// Use window.debounce if available (from utilities.js), otherwise create a local version
// Use a local variable to avoid conflicts with global function declarations
const debounceFunc = (typeof window !== 'undefined' && window.debounce) ? window.debounce : function(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Use window.onKeyUpEscape if available, otherwise use local version
// Use a local variable to avoid conflicts with global function declarations
const onKeyUpEscapeFunc = (typeof window !== 'undefined' && window.onKeyUpEscape) ? window.onKeyUpEscape : function(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
};

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Use shorter debounce for better responsiveness
    this.debouncedOnSubmit = debounceFunc((event) => {
      this.onSubmitHandler(event);
    }, 300);
    
    // Immediate handler for checkboxes (no debounce)
    this.immediateOnSubmit = (event) => {
      this.onSubmitHandler(event);
    };

    const facetForm = this.querySelector('form');
    if (facetForm) {
      // Listen for 'input' events (for text/range inputs) with debounce
      facetForm.addEventListener('input', (e) => {
        // Handle sort dropdown immediately, other inputs with debounce
        if (e.target.name === 'sort_by' || e.target.id === 'SortBy' || e.target.closest('custom-select')) {
          this.immediateOnSubmit(e);
        } else {
          this.debouncedOnSubmit(e);
        }
      });
      
      // Listen for 'change' events (for checkboxes/selects) - use immediate handler for checkboxes and sort
      facetForm.addEventListener('change', (e) => {
        // For checkboxes and sort dropdowns, use immediate handler; for other inputs, use debounced
        if (e.target.type === 'checkbox' || e.target.name === 'sort_by' || e.target.id === 'SortBy') {
          this.immediateOnSubmit(e);
        } else {
          this.debouncedOnSubmit(e);
        }
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscapeFunc);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collectionGridContainer = productGridContainer.querySelector('.collection-grid-container');
      if (collectionGridContainer) {
        collectionGridContainer.classList.add('loading');
      }
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      // Ensure searchParams is properly formatted
      // If it's already a query string, use it directly; otherwise construct it properly
      const queryString = searchParams && searchParams.length > 0 
        ? (searchParams.startsWith('?') ? searchParams.slice(1) : searchParams)
        : '';
      
      const url = queryString 
        ? `${window.location.pathname}?section_id=${section.section}&${queryString}`
        : `${window.location.pathname}?section_id=${section.section}`;
      
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    console.log('Fetching URL:', url);
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        
        // Ensure loading state is removed
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) {
          const collectionGridContainer = productGridContainer.querySelector('.collection-grid-container');
          if (collectionGridContainer) {
            collectionGridContainer.classList.remove('loading');
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching section:', error);
        // Remove loading state on error
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) {
          const collectionGridContainer = productGridContainer.querySelector('.collection-grid-container');
          if (collectionGridContainer) {
            collectionGridContainer.classList.remove('loading');
          }
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cachedData = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cachedData || !cachedData.html) return;
    
    const html = cachedData.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) {
      console.warn('ProductGridContainer not found');
      return;
    }

    const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
    const parsedProductGridContainer = parsedDoc.getElementById('ProductGridContainer');
    if (!parsedProductGridContainer) {
      console.warn('ProductGridContainer not found in parsed HTML');
      return;
    }

    // Store the current innerHTML for debugging
    const beforeHTML = productGridContainer.innerHTML.length;

    // Replace content
    productGridContainer.innerHTML = parsedProductGridContainer.innerHTML;

    // Check if products were actually rendered
    const productGrid = productGridContainer.querySelector('#product-grid');
    const productItems = productGrid ? productGrid.querySelectorAll('.grid__item') : [];
    console.log('Products rendered:', productItems.length, 'items');

    // Ensure product grid is visible
    if (productGrid) {
      productGrid.style.display = '';
      productGrid.style.visibility = '';
      productGrid.style.opacity = '';
      productGrid.style.height = '';
    }

    // Remove loading state and ensure collection-grid-container is visible
    const collectionGridContainer = productGridContainer.querySelector('.collection-grid-container');
    if (collectionGridContainer) {
      collectionGridContainer.classList.remove('loading');
      collectionGridContainer.style.display = '';
      collectionGridContainer.style.visibility = '';
      collectionGridContainer.style.opacity = '';
    }

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    rebindSortDropdown();

    // Force load images using global function with queue management
    if (typeof window.forceLoadProductImages === 'function') {
      setTimeout(() => {
        // Setup observer for new products
        if (typeof window.setupProductImageObserver === 'function') {
          window.setupProductImageObserver();
        }
        window.forceLoadProductImages();
      }, 100);
    }
  }

  static renderProductCount(html) {
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
    const countElement = parsedDoc.getElementById('ProductCount');
    if (!countElement) return;
    
    const count = countElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter');

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        // Preserve price input values before updating (they get lost when innerHTML is replaced)
        const priceRange = currentElement.querySelector('price-range');
        let savedPriceValues = null;
        if (priceRange) {
          const priceInputs = priceRange.querySelectorAll('input[type="number"]');
          if (priceInputs.length >= 2) {
            savedPriceValues = {
              min: priceInputs[0].value,
              max: priceInputs[1].value,
              minName: priceInputs[0].name,
              maxName: priceInputs[1].name
            };
            console.log('Preserving price values:', savedPriceValues);
          }
        }
        
        // Also check URL parameters as backup source for price values
        const urlParams = new URLSearchParams(window.location.search);
        if (savedPriceValues) {
          // Update from URL if URL has values (URL is source of truth)
          const urlMinValue = urlParams.get(savedPriceValues.minName);
          const urlMaxValue = urlParams.get(savedPriceValues.maxName);
          if (urlMinValue) {
            savedPriceValues.min = urlMinValue;
            console.log('Updated min from URL:', urlMinValue);
          }
          if (urlMaxValue) {
            savedPriceValues.max = urlMaxValue;
            console.log('Updated max from URL:', urlMaxValue);
          }
        } else {
          // If no saved values, try to get from URL
          const priceRangeElement = currentElement.querySelector('price-range');
          if (priceRangeElement) {
            const existingInputs = priceRangeElement.querySelectorAll('input[type="number"]');
            if (existingInputs.length >= 2) {
              const urlMinValue = urlParams.get(existingInputs[0].name);
              const urlMaxValue = urlParams.get(existingInputs[1].name);
              if (urlMinValue || urlMaxValue) {
                savedPriceValues = {
                  min: urlMinValue || existingInputs[0].value,
                  max: urlMaxValue || existingInputs[1].value,
                  minName: existingInputs[0].name,
                  maxName: existingInputs[1].name
                };
                console.log('Got price values from URL:', savedPriceValues);
              }
            }
          }
        }
        
        // Update the innerHTML
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        
        // Restore price input values if they were saved (preserve user input)
        if (savedPriceValues) {
          const newPriceRange = currentElement.querySelector('price-range');
          if (newPriceRange) {
            const newPriceInputs = newPriceRange.querySelectorAll('input[type="number"]');
            if (newPriceInputs.length >= 2) {
              // Restore min value if it was set and matches the name
              if (newPriceInputs[0].name === savedPriceValues.minName && savedPriceValues.min && savedPriceValues.min.trim() !== '') {
                newPriceInputs[0].value = savedPriceValues.min;
                // Set attribute to prevent clearing
                newPriceInputs[0].setAttribute('data-preserved-value', savedPriceValues.min);
                console.log('Restored min price:', savedPriceValues.min, 'to input:', newPriceInputs[0].name);
              }
              // Restore max value if it was set and matches the name
              if (newPriceInputs[1].name === savedPriceValues.maxName && savedPriceValues.max && savedPriceValues.max.trim() !== '') {
                newPriceInputs[1].value = savedPriceValues.max;
                // Set attribute to prevent clearing
                newPriceInputs[1].setAttribute('data-preserved-value', savedPriceValues.max);
                console.log('Restored max price:', savedPriceValues.max, 'to input:', newPriceInputs[1].name);
              }
              
              // Re-initialize the price-range component AFTER setting values
              if (newPriceRange.setMinAndMaxValues) {
                newPriceRange.setMinAndMaxValues();
              }
              
              // Also call preserveValuesFromURL if it exists
              if (newPriceRange.preserveValuesFromURL) {
                newPriceRange.preserveValuesFromURL();
              }
              
              // Force restore after delays to catch any clearing (multiple checks)
              const restoreValues = () => {
                if (savedPriceValues.min && newPriceInputs[0].value !== savedPriceValues.min) {
                  console.warn('Min price was cleared! Restoring from preserved value:', savedPriceValues.min);
                  newPriceInputs[0].value = savedPriceValues.min;
                  newPriceInputs[0].setAttribute('data-preserved-value', savedPriceValues.min);
                }
                if (savedPriceValues.max && newPriceInputs[1].value !== savedPriceValues.max) {
                  console.warn('Max price was cleared! Restoring from preserved value:', savedPriceValues.max);
                  newPriceInputs[1].value = savedPriceValues.max;
                  newPriceInputs[1].setAttribute('data-preserved-value', savedPriceValues.max);
                }
              };
              
              // Check multiple times to catch delayed clearing
              setTimeout(restoreValues, 100);
              setTimeout(restoreValues, 300);
              setTimeout(restoreValues, 500);
              setTimeout(restoreValues, 1000);
              
              // Add a mutation observer to watch for value changes
              const minObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                  if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                    if (savedPriceValues.min && newPriceInputs[0].value !== savedPriceValues.min) {
                      console.warn('Min price attribute changed! Restoring:', savedPriceValues.min);
                      newPriceInputs[0].value = savedPriceValues.min;
                    }
                  }
                });
              });
              
              const maxObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                  if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                    if (savedPriceValues.max && newPriceInputs[1].value !== savedPriceValues.max) {
                      console.warn('Max price attribute changed! Restoring:', savedPriceValues.max);
                      newPriceInputs[1].value = savedPriceValues.max;
                    }
                  }
                });
              });
              
              // Observe value attribute changes
              minObserver.observe(newPriceInputs[0], { attributes: true, attributeFilter: ['value'] });
              maxObserver.observe(newPriceInputs[1], { attributes: true, attributeFilter: ['value'] });
              
              // Store observers for cleanup (optional, but good practice)
              newPriceRange._priceObservers = [minObserver, maxObserver];
              
              // Add input event listeners to prevent clearing
              const preventMinClear = (e) => {
                if (savedPriceValues.min && !e.target.value && e.target.value !== savedPriceValues.min) {
                  const urlParams = new URLSearchParams(window.location.search);
                  const urlMinValue = urlParams.get(savedPriceValues.minName);
                  if (urlMinValue) {
                    console.warn('Preventing min price clear. Restoring:', urlMinValue);
                    e.target.value = urlMinValue;
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }
              };
              
              const preventMaxClear = (e) => {
                if (savedPriceValues.max && !e.target.value && e.target.value !== savedPriceValues.max) {
                  const urlParams = new URLSearchParams(window.location.search);
                  const urlMaxValue = urlParams.get(savedPriceValues.maxName);
                  if (urlMaxValue) {
                    console.warn('Preventing max price clear. Restoring:', urlMaxValue);
                    e.target.value = urlMaxValue;
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }
              };
              
              // Listen for input events to catch clearing attempts
              newPriceInputs[0].addEventListener('input', preventMinClear, { passive: false });
              newPriceInputs[1].addEventListener('input', preventMaxClear, { passive: false });
              
              // Also listen for blur to restore if cleared
              newPriceInputs[0].addEventListener('blur', () => {
                if (savedPriceValues.min && !newPriceInputs[0].value) {
                  const urlParams = new URLSearchParams(window.location.search);
                  const urlMinValue = urlParams.get(savedPriceValues.minName);
                  if (urlMinValue) {
                    console.warn('Min price cleared on blur. Restoring:', urlMinValue);
                    newPriceInputs[0].value = urlMinValue;
                  }
                }
              });
              
              newPriceInputs[1].addEventListener('blur', () => {
                if (savedPriceValues.max && !newPriceInputs[1].value) {
                  const urlParams = new URLSearchParams(window.location.search);
                  const urlMaxValue = urlParams.get(savedPriceValues.maxName);
                  if (urlMaxValue) {
                    console.warn('Max price cleared on blur. Restoring:', urlMaxValue);
                    newPriceInputs[1].value = urlMaxValue;
                  }
                }
              });
            }
          }
        }
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        /*
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
        */
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender && event && event.target) {
      const closestJSFilter = event.target.closest('.js-filter');
      if (closestJSFilter && closestJSFilter.id) {
        const closestJSFilterID = closestJSFilter.id;
        FacetFiltersForm.renderCounts(countsToRender, closestJSFilter);

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        if (newFacetDetailsElement) {
          const newElementToActivate = newFacetDetailsElement.querySelector('.facets__summary');

          const isTextInput = event.target.getAttribute('type') === 'text';

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !productGrid.dataset.id) {
      return [];
    }
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    // console.log(new URLSearchParams(formData).toString());
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    // Handle label clicks - find the associated checkbox
    let targetElement = event.target;
    if (targetElement.tagName === 'LABEL') {
      const checkboxId = targetElement.getAttribute('for');
      if (checkboxId) {
        targetElement = document.getElementById(checkboxId);
      }
    }
    
    // Note: Removed handleSingleSelection - filters should allow multiple selections
    // If single selection is needed for specific filters, it should be handled differently
    
    // get the current parameters
    const currentParams = new URLSearchParams(window.location.search);
    // clear all filter.v. parameters
    Array.from(currentParams.keys()).forEach(key => {
      if (key.startsWith('filter.v.')) {
        currentParams.delete(key);
      }
    });
    
    // let's add the new parameters
    const params = new URLSearchParams();
    
    // Get all forms within facet-filters-form elements (both current and all on page)
    const allFacetForms = document.querySelectorAll('facet-filters-form');
    const sortFilterForms = [];
    
    // Collect all forms from all facet-filters-form elements
    allFacetForms.forEach(facetFiltersForm => {
      const form = facetFiltersForm.querySelector('form');
      if (form) {
        sortFilterForms.push(form);
      }
    });

    // iterate over all forms
    sortFilterForms.forEach((form) => {
      // Get sort_by value from form (check both select and custom-select)
      // Priority: 1) custom-select dataset.value, 2) select element's selected option value
      let sortValue = null;
      
      const customSelect = form.querySelector('custom-select');
      if (customSelect && customSelect.dataset.value) {
        sortValue = customSelect.dataset.value;
      }
      
      if (!sortValue) {
        const sortSelect = form.querySelector('select[name="sort_by"], select#SortBy');
        if (sortSelect) {
          // Get from selected option (most reliable)
          const selectedOption = sortSelect.options[sortSelect.selectedIndex];
          if (selectedOption) {
            sortValue = selectedOption.value;
          } else if (sortSelect.value) {
            sortValue = sortSelect.value;
          }
        }
      }
      
      if (sortValue) {
        params.set('sort_by', sortValue);
      }
      
      // get all checkbox inputs
      const checkboxInputs = form.querySelectorAll('input[type="checkbox"]');
      // iterate over all checked checkboxes
      checkboxInputs.forEach(input => {
        if (input.checked) {
          params.append(input.name, input.value);
        }
      });
      // get all price inputs (exclude checkboxes and hidden inputs that aren't price filters)
      const priceInputs = form.querySelectorAll('price-range input[type="number"], input[type="number"][name*="filter.p.price"]');
      priceInputs.forEach(input => {
        // Include price inputs if they have a valid numeric value (including 0, but not empty)
        const numValue = parseFloat(input.value);
        if (!isNaN(numValue) && numValue >= 0 && input.value.trim() !== '') {
          params.set(input.name, input.value);
          console.log('Including price input:', input.name, '=', input.value);
        } else if (input.value && input.value.trim() !== '' && !isNaN(parseFloat(input.value))) {
          // Include non-empty numeric string values
          params.set(input.name, input.value);
          console.log('Including price input (formatted):', input.name, '=', input.value);
        }
      });
    });
    
    // If no sort_by was found in forms, keep the existing one from URL
    if (!params.has('sort_by')) {
      const sortParam = currentParams.get('sort_by');
      if (sortParam) {
        params.set('sort_by', sortParam);
      }
    }
    
    // add the new parameters to the current parameters
    this.onSubmitForm(params.toString(), event);
  }

  // Removed handleSingleSelection - filters should allow multiple selections by default
  // If you need single-selection filters, implement it per-filter-type or use radio buttons instead

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    // Preserve values from URL on initialization
    this.preserveValuesFromURL();
  }
  
  preserveValuesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const inputs = this.querySelectorAll('input');
    if (inputs.length >= 2) {
      const minInput = inputs[0];
      const maxInput = inputs[1];
      const urlMinValue = urlParams.get(minInput.name);
      const urlMaxValue = urlParams.get(maxInput.name);
      
      if (urlMinValue && !minInput.value) {
        minInput.value = urlMinValue;
        console.log('PriceRange: Restored min from URL:', urlMinValue);
      }
      if (urlMaxValue && !maxInput.value) {
        maxInput.value = urlMaxValue;
        console.log('PriceRange: Restored max from URL:', urlMaxValue);
      }
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }
  
  // Prevent clearing of preserved values
  preventValueClearing(input, preservedValue) {
    if (preservedValue && input.value !== preservedValue) {
      // Check if this is an intentional user change or an accidental clear
      const urlParams = new URLSearchParams(window.location.search);
      const urlValue = urlParams.get(input.name);
      
      // If URL has the value but input doesn't, restore it
      if (urlValue && !input.value) {
        console.warn('Preventing clear of', input.name, 'from', preservedValue, 'to empty. Restoring:', urlValue);
        input.value = urlValue;
        return false; // Prevent the change
      }
    }
    return true; // Allow the change
  }

  onKeyDown(event) {
    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Escape'];
    if (!allowedKeys.includes(event.key) && !/[0-9.,]/.test(event.key)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) {
      minInput.setAttribute('data-max', maxInput.value);
    } else {
      minInput.setAttribute('data-max', maxInput.getAttribute('max'));
    }

    if (minInput.value) {
      maxInput.setAttribute('data-min', minInput.value);
    } else {
      maxInput.setAttribute('data-min', minInput.getAttribute('min'));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min')) || 0; // Default to input's min attribute
    const max = Number(input.getAttribute('max')); // Default to input's max attribute

    if (!isNaN(min) && value < min) input.value = min;
    if (!isNaN(max) && value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Track if sort dropdown is already bound to avoid duplicate listeners
let sortDropdownBound = false;
const boundSortSelects = new WeakSet();

function rebindSortDropdown() {
  // Try multiple selectors to find the sorting dropdown
  const sortSelect = document.querySelector('select#SortBy') || 
                    document.querySelector('select[name="sort_by"]') ||
                    document.querySelector('.facets__sort-input');
  
  if (!sortSelect) {
    return;
  }

  // Check if already bound to avoid duplicate listeners
  if (boundSortSelects.has(sortSelect)) {
    return;
  }
  
  // Sync select value with URL parameter on page load
  const urlParams = new URLSearchParams(window.location.search);
  const urlSortBy = urlParams.get('sort_by');
  if (urlSortBy) {
    // Find the option with matching value
    const option = Array.from(sortSelect.options).find(opt => opt.value === urlSortBy);
    if (option) {
      const optionIndex = Array.from(sortSelect.options).indexOf(option);
      if (optionIndex >= 0) {
        sortSelect.selectedIndex = optionIndex;
        sortSelect.value = urlSortBy;
      }
      
      // Also update custom-select if it exists
      const customSelect = sortSelect.closest('custom-select');
      if (customSelect) {
        customSelect.dataset.value = urlSortBy;
        // Update the button text
        const btnToggle = customSelect.querySelector('.js-btn-dropdown');
        if (btnToggle) {
          const iconChevron = customSelect.dataset.iconChevronDown || '';
          btnToggle.innerHTML = option.textContent + iconChevron;
        }
      }
    }
  }
  
  // Also sync price filter values from URL parameters
  const priceRanges = document.querySelectorAll('price-range');
  priceRanges.forEach(priceRange => {
    const priceInputs = priceRange.querySelectorAll('input[type="number"]');
    if (priceInputs.length >= 2) {
      const minInput = priceInputs[0];
      const maxInput = priceInputs[1];
      
      // Get price values from URL if they exist
      if (minInput.name) {
        const urlMinValue = urlParams.get(minInput.name);
        if (urlMinValue && urlMinValue.trim() !== '') {
          minInput.value = urlMinValue;
        }
      }
      if (maxInput.name) {
        const urlMaxValue = urlParams.get(maxInput.name);
        if (urlMaxValue && urlMaxValue.trim() !== '') {
          maxInput.value = urlMaxValue;
        }
      }
      
      // Re-initialize the price-range component
      if (priceRange.setMinAndMaxValues) {
        priceRange.setMinAndMaxValues();
      }
    }
  });
  
  // Use debouncing to avoid multiple triggers
  let timeoutId;
  
  const handleSortChange = function(event) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      // Get value from select element - always read directly from the select element
      // as it's the most reliable source (custom-select may not always update dataset.value)
      let sortValue = null;
      
      // Always read from the actual select element's selected option (most reliable)
      if (sortSelect && sortSelect.options && sortSelect.options.length > 0) {
        const selectedIndex = sortSelect.selectedIndex;
        if (selectedIndex >= 0 && selectedIndex < sortSelect.options.length) {
          const selectedOption = sortSelect.options[selectedIndex];
          if (selectedOption && selectedOption.value) {
            sortValue = selectedOption.value;
            console.log('Read from selectedIndex:', selectedIndex, 'value:', sortValue);
          }
        }
      }
      
      // Fallback to select.value if selectedIndex didn't work
      if (!sortValue && sortSelect && sortSelect.value) {
        sortValue = sortSelect.value;
        console.log('Read from select.value:', sortValue);
      }
      
      // Debug: Log all available options for problematic values
      if (!sortValue || sortValue === 'price-ascending' || sortValue === 'created-descending') {
        console.log('All available sort options:', Array.from(sortSelect.options).map((opt, idx) => ({
          index: idx,
          value: opt.value,
          text: opt.textContent,
          selected: opt.selected
        })));
        console.log('Current selectedIndex:', sortSelect.selectedIndex);
        console.log('Current select.value:', sortSelect.value);
      }
      
      // Final fallback: check custom-select dataset (but this is less reliable)
      if (!sortValue) {
        const customSelect = sortSelect.closest('custom-select');
        if (customSelect && customSelect.dataset.value) {
          sortValue = customSelect.dataset.value;
        }
      }
      
      // Last resort: check event target
      if (!sortValue && event.target) {
        if (event.target.tagName === 'SELECT') {
          const selectedOption = event.target.options[event.target.selectedIndex];
          sortValue = selectedOption ? selectedOption.value : event.target.value;
        } else {
          const customSelectFromEvent = event.target.closest('custom-select');
          if (customSelectFromEvent) {
            const actualSelect = customSelectFromEvent.querySelector('select');
            if (actualSelect) {
              const selectedOption = actualSelect.options[actualSelect.selectedIndex];
              sortValue = selectedOption ? selectedOption.value : actualSelect.value;
            } else if (customSelectFromEvent.dataset.value) {
              sortValue = customSelectFromEvent.dataset.value;
            }
          }
        }
      }
      
      if (!sortValue) {
        console.warn('Sort value not found', { sortSelect, event: event.target });
        return;
      }
      
      // Ensure the value is properly encoded (URLSearchParams handles this, but let's be explicit)
      sortValue = sortValue.trim();
      
      // Debug log to help identify issues
      console.log('Sort change detected:', { sortValue, selectElement: sortSelect, selectedIndex: sortSelect.selectedIndex });
      
      // Build params with current filters + new sort value
      const currentParams = new URLSearchParams(window.location.search);
      const params = new URLSearchParams();
      
      // Preserve all existing filter parameters
      currentParams.forEach((value, key) => {
        if (key !== 'sort_by') {
          if (key.startsWith('filter.v.')) {
            params.append(key, value);
          } else {
            params.set(key, value);
          }
        }
      });
      
      // Add the new sort_by value (URLSearchParams will handle encoding)
      params.set("sort_by", sortValue);
      
      const paramsString = params.toString();
      console.log('Sort change - Value:', sortValue, 'Params:', paramsString);
      
      // Force AJAX method for sorting
      if (typeof FacetFiltersForm !== "undefined" && FacetFiltersForm.renderPage) {
        const url = new URL(window.location.href);
        url.search = paramsString;
        
        // Update URL first
        history.pushState({}, '', url.toString());
        
        // Debug: Log the URL that will be requested
        const sections = FacetFiltersForm.getSections();
        if (sections.length > 0) {
          const ajaxUrl = `${window.location.pathname}?section_id=${sections[0].section}&${paramsString}`;
          console.log('AJAX URL:', ajaxUrl);
          console.log('Sort value being sent:', sortValue);
        } else {
          console.error('No sections found!');
        }
        
        // Trigger AJAX render with all parameters
        FacetFiltersForm.renderPage(paramsString, event);
        
        // Force show products after AJAX
        setTimeout(() => {
          const productGrid = document.getElementById('product-grid');
          if (productGrid) {
            productGrid.style.display = 'block';
            productGrid.style.visibility = 'visible';
            productGrid.style.opacity = '1';
          }
          
          // Show all products in grid
          const gridItems = productGrid.querySelectorAll('.grid__item');
          gridItems.forEach(item => {
            item.style.display = 'block';
            item.style.visibility = 'visible';
          });
          
          // Hide load more if not needed
          const loadMoreBtn = document.querySelector('.js-btn-load-more');
          if (loadMoreBtn && gridItems.length <= 12) {
            loadMoreBtn.style.display = 'none';
          }
        }, 300);
        
      } else {
        window.location = url.toString();
      }
    }, 100); // 100ms debounce
  };
  
  // Listen for both 'input' (from custom-select) and 'change' events
  sortSelect.addEventListener('input', handleSortChange);
  sortSelect.addEventListener('change', handleSortChange);
  
  // Also listen on the custom-select wrapper if it exists
  const customSelectWrapper = sortSelect.closest('custom-select');
  if (customSelectWrapper && !boundSortSelects.has(customSelectWrapper)) {
    customSelectWrapper.addEventListener('input', handleSortChange);
    boundSortSelects.add(customSelectWrapper);
  }
  
  // Mark as bound
  boundSortSelects.add(sortSelect);
  
  // Remove any existing onchange that causes page reload
  sortSelect.removeAttribute('onchange');
}

// This function is no longer needed - using inline event handler above

// Initialize on page load
document.addEventListener("DOMContentLoaded", rebindSortDropdown);

// Also try after a short delay in case custom-select loads later
setTimeout(rebindSortDropdown, 1000);